import { useQuery, useMutation, gql } from '@apollo/client';
import React, { useState } from 'react';
import { Button, Card, message, Row, Steps } from 'antd';
import { useNavigate } from 'react-router';
import { useIntl } from 'react-intl';
import FileStep from './FileStep';
import ChooseTemplate from './ChooseTemplate';
import VisualizeTemplate from './VisualizeTemplate';
import ValidationOfFile from './ValidationOfFile';
import CustomFieldsImportMapping, { CustomFieldsImportMappingRow } from './CustomFieldsImportMapping';
import { usePageTitle } from '../../../hooks/usePageTitle';
import MainView from '../../../components/View/MainView';
import { Locale } from '../../../../localization/LocalizationKeys';
import { ImportTemplatesQuery } from '../../../../gql/typings';

const Uploader = () => {
  const navigate = useNavigate();
  const { data, loading } = useQuery<ImportTemplatesQuery>(DATA_QUERY);
  const [saveProcessorData, { loading: savingMapping }] = useMutation(SAVE_CUSTOM_FIELDS_MAPPING_MUTATION);
  const [templateId, setTemplateId] = useState<number>();
  const [projectId, setProjectId] = useState<number>();
  const [step, setStep] = useState(0);
  const [customFieldRows, setCustomFieldRows] = useState<CustomFieldsImportMappingRow[]>([]);
  const intl = useIntl();
  usePageTitle('Import');

  const selectedTemplate = data?.importTemplates.nodes.find(n => n.id === templateId);
  const customFieldsProcess = selectedTemplate?.processes.nodes
    .find(process => (process as typeof process & {
      requiresCustomFieldsImportMapping?: boolean;
    }).requiresCustomFieldsImportMapping);
  const customFieldsProcessorId = Number((customFieldsProcess as typeof customFieldsProcess & {
    processorId?: string|number;
  })?.processorId);
  const customFieldsProcessorDataKey = (customFieldsProcess as typeof customFieldsProcess & {
    processorDataKey?: string|null;
  })?.processorDataKey;
  const isCustomFieldsImport = Boolean(customFieldsProcess);
  const customFieldsFixedDbColumns = [...(customFieldsProcess?.fields.nodes ?? [])]
    .sort((a, b) => a.lineDataIndex - b.lineDataIndex);
  const firstCustomFieldColumn = customFieldsFixedDbColumns
    .reduce((max, field) => Math.max(max, field.lineDataIndex), -1) + 1;

  const continueToProcessing = async () => {
    if (!projectId) return;

    if (!isCustomFieldsImport) {
      navigate(`/import/${projectId}/process`);
      return;
    }

    const invalidRows = customFieldRows.filter(
      row => row.columnIndex === undefined || row.columnIndex < firstCustomFieldColumn || !row.customFieldCode
    );
    if (invalidRows.length > 0) {
      message.error(`Select CSV column ${firstCustomFieldColumn} or higher and custom field for each custom-field row.`);
      return;
    }

    const duplicateColumns = customFieldRows
      .map(row => row.columnIndex)
      .filter((columnIndex, index, source) => columnIndex !== undefined && source.indexOf(columnIndex) !== index);

    if (duplicateColumns.length > 0) {
      message.error('Each custom field row must use a different CSV column.');
      return;
    }

    if (!Number.isFinite(customFieldsProcessorId)) {
      message.error('Custom import template is missing processor id.');
      return;
    }

    if (!customFieldsProcessorDataKey) {
      message.error('Custom import template is missing processor data key.');
      return;
    }

    await saveProcessorData({
      variables: {
        key: customFieldsProcessorDataKey,
        processorId: customFieldsProcessorId,
        projectId,
        value: JSON.stringify({
          customFields: customFieldRows.map(row => ({
            columnIndex: row.columnIndex,
            customFieldCode: row.customFieldCode,
            addNewTextValue: row.addNewTextValue,
            addNewOption: row.addNewOption,
            replace: row.replace,
          })),
        }),
      },
    });

    navigate(`/import/${projectId}/process`);
  };

  const upper = (stepId: number) => {
    if (stepId === 0) return (
      <ChooseTemplate
        data={data}
        loading={loading}
        templateId={templateId}
        setTemplateId={setTemplateId}
        done={() => setStep(1)}
      />
    );
    if (stepId === 1) return null;
    if (stepId === 2) return (
      <Row style={{ textAlign: 'center' }}>
        <Button
          type="primary"
          onClick={continueToProcessing}
          loading={savingMapping}
        >
          {intl.formatMessage(Locale.Command.Continue)}
        </Button>
      </Row>
    );
  };

  const lower = (stepId: number) => {
    if (!data) return <></>;
    if (stepId === 0) return (
      templateId && <Card>
        <VisualizeTemplate template={data.importTemplates.nodes.find(n => n.id === templateId)!} />
      </Card>
    );
    if (!templateId) return <></>;
    if (stepId === 1) return (
      <Card>
        <FileStep
          templateId={templateId}
          setProjectId={setProjectId}
          done={() => setStep(2)}
        />
      </Card>
    );
    if (!projectId) return <></>;
    if (stepId === 2) return (
      <>
        <Card>
          <ValidationOfFile projectId={projectId} showAllColumns={isCustomFieldsImport} />
        </Card>
        {isCustomFieldsImport && (
          <>
            <br />
            <Card>
              <CustomFieldsImportMapping
                rows={customFieldRows}
                setRows={setCustomFieldRows}
                fixedColumns={customFieldsFixedDbColumns}
                firstCustomFieldColumn={firstCustomFieldColumn}
              />
            </Card>
          </>
        )}
      </>
    );
  };


  return (
    <MainView title="Import" className="importer-container">
      <Card>
        <Steps
          current={step}
          items={[
            {
              title: intl.formatMessage(Locale.Command.Select_template)
            },
            {
              title: intl.formatMessage(Locale.Command.File_upload)
            },
            {
              title: intl.formatMessage(Locale.Command.File_preview)
            },
            {
              title: intl.formatMessage(Locale.Command.Start_Processing)
            },
          ]}
        />
        <br />
        {upper(step)}
      </Card>
      <br />
      {lower(step)}
    </MainView>
  );
};

const DATA_QUERY = gql`
  query ImportTemplates {
    importTemplates {
      hash
      totalCount
      nodes {
        id
        name
        note
        processes {
          hash
          nodes {
            id
            processorId
            processorDataKey
            requiresCustomFieldsImportMapping
            heading
            note
            sort
            fields {
              hash
              nodes {
                id
                lineDataIndex
                field {
                  code
                  heading
                  note
                }
              }
            }
          }
        }
      }
    }
  }
`;

const SAVE_CUSTOM_FIELDS_MAPPING_MUTATION = gql`
  mutation SaveCustomFieldsImportMapping($key: String!, $processorId: Int!, $projectId: Int!, $value: String) {
    saveImportProcessorData(key: $key, processorId: $processorId, projectId: $projectId, value: $value) {
      id
      value
    }
  }
`;

export default Uploader;
